import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/api/client";
import { CheckCircle2, Truck, PackageCheck, PackageOpen, XCircle } from "lucide-react";
import { PaymentStatusBadge } from "@/components/OrderStagePill";

const STATUS_LABEL = { awaiting_payment: "Awaiting payment", awaiting_vendor: "Awaiting vendor confirmation", processing: "Confirmed by vendor", confirmed: "Confirmed", packed: "Packed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled", payment_failed: "Payment failed" };
const STATUS_TONE = {
  awaiting_payment: "bg-gray-50 text-gray-600 border-gray-200",
  awaiting_vendor: "bg-orange-50 text-orange-700 border-orange-200",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  payment_failed: "bg-red-50 text-red-700 border-red-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  packed: "bg-purple-50 text-purple-700 border-purple-200",
  shipped: "bg-amber-50 text-amber-700 border-amber-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const STEPS = [
  { key: "awaiting_vendor", label: "Awaiting vendor", Icon: CheckCircle2 },
  { key: "processing", label: "Confirmed", Icon: CheckCircle2 },
  { key: "packed", label: "Packed", Icon: PackageOpen },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: PackageCheck },
];

export function StatusPill({ status, testId }) {
  const cls = STATUS_TONE[status] || "bg-gray-50 text-gray-600 border-gray-200";
  return (
    <span data-testid={testId} className={`inline-block text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function BuyerOrderTracker({ order, testIdPrefix = "buyer-track" }) {
  if (!order) return null;
  const status = order.status;
  const isCancelled = status === "cancelled";
  const currentIdx = STEPS.findIndex((s) => s.key === (status === "confirmed" ? "processing" : status));
  return (
    <div className="mt-4" data-testid={`${testIdPrefix}-${order.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs uppercase tracking-widest text-gray-500">Live status</span>
        <StatusPill status={status} testId={`${testIdPrefix}-badge-${order.id}`} />
        <PaymentStatusBadge order={order} />
      </div>
      {isCancelled ? (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <XCircle className="h-4 w-4" />
          <span className="text-sm">This order was cancelled. Any charged amount will be refunded within 5-7 business days.</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 sm:gap-2">
          {STEPS.map((s, idx) => {
            const done = currentIdx >= idx;
            const Icon = s.Icon;
            return (
              <div key={s.key} className="flex-1 flex items-center gap-1 sm:gap-2">
                <div data-testid={`${testIdPrefix}-step-${s.key}-${order.id}`} className={`h-8 w-8 rounded-full grid place-items-center border-2 transition-colors ${done ? "bg-[#F48E2E] border-[#F48E2E] text-white" : "bg-white border-gray-200 text-gray-400"}`}>
                  <Icon className="h-4 w-4" />
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full ${currentIdx > idx ? "bg-[#F48E2E]" : "bg-gray-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {!isCancelled && (
        <div className="mt-1 flex text-[10px] uppercase tracking-widest text-gray-500">
          {STEPS.map((s) => (
            <div key={s.key} className="flex-1">{s.label}</div>
          ))}
        </div>
      )}
      {(order.tracking_number || order.courier) && (
        <div className="mt-4 rounded-xl border border-[#F48E2E]/30 bg-[#FEF3E6] px-3 py-2 text-sm" data-testid={`${testIdPrefix}-tracking-${order.id}`}>
          <div className="text-[11px] uppercase tracking-widest text-[#F48E2E] font-bold">Tracking</div>
          <div className="mt-1 space-y-0.5">
            {order.courier && <div><span className="text-gray-500 text-xs">Courier:</span> <span className="font-medium">{order.courier}</span></div>}
            {order.tracking_number && <div><span className="text-gray-500 text-xs">Tracking #:</span> <span className="font-mono-tab">{order.tracking_number}</span></div>}
          </div>
        </div>
      )}
      {(order.status_history || []).length > 0 && (
        <details className="mt-3" data-testid={`${testIdPrefix}-history-${order.id}`}>
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#F48E2E]">Full history ({order.status_history.length})</summary>
          <div className="mt-2 space-y-1 pl-3 border-l-2 border-[#F48E2E]/30">
            {order.status_history.slice().reverse().map((h, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium capitalize">{h.status}</span>
                {h.tracking_number && <span className="text-gray-600"> · tracking {h.tracking_number}</span>}
                {h.courier && <span className="text-gray-600"> · {h.courier}</span>}
                <span className="text-gray-400 ml-2">{new Date(h.updated_at).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// Poll hook to keep buyer tracking pages live without websockets.
export function useOrderPolling(fetcher, intervalMs = 15000, deps = []) {
  const timerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try { await fetcher(); } catch { /* ignore */ }
    };
    tick();
    timerRef.current = setInterval(tick, intervalMs);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useLiveOrders(pathnameDep) {
  const [orders, setOrders] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useMemo(() => async () => {
    const [o, n] = await Promise.all([api.get("/orders/mine"), api.get("/notifications")]);
    setOrders(o.data);
    setNotifs(n.data);
    setLoading(false);
  }, []);
  useOrderPolling(load, 15000, [pathnameDep]);
  return { orders, notifs, loading, reload: load };
}
